import { inject, Injectable, OnInit } from '@angular/core';
import { SAVE_SERVICE } from '@core/saves/saves.interface';
import { LpWoordData } from '@typings/lp/generated';

export interface LabelStatistic {
  label: string;
  attemptCount: number;
  weight: number;
  lastAttemptDate: string;
}

@Injectable({
  providedIn: 'root',
})
export class AdaptivityService implements OnInit {
  saver = inject(SAVE_SERVICE);

  // map with the label as id
  private labelStatistics: Record<string, LabelStatistic> = {};

  // TODO: volgens de documentatie van olivier (https://app.clickup.com/4767728/v/dc/4hfzg-137772/4hfzg-84552) moet dit 7 of 8 zijn.
  private readonly LAST_N_ATTEMPTS = 5; // number of attempts considered
  private readonly INITIAL_WEIGHT = 10; // high weight for unseen labels
  private readonly PERFORMANCE_MAX_BONUS = 10;
  private readonly RECENCY_MAX_BONUS = 10;
  private readonly RECENCY_CAP = 60 * 48; // maxes out after 48 hours

  // TODO: onInit will not work on @Injectable.
  // eslint-disable-next-line @angular-eslint/contextual-lifecycle
  async ngOnInit() {
    //load the previous save using the saver
    const previousSave = await this.saver.load();
    // load the label statistics from the previous save
    this.labelStatistics = previousSave.labelStatistics.reduce(
      (acc, stat) => {
        acc[stat.label] = stat;
        return acc;
      },
      {} as Record<string, LabelStatistic>,
    );
  }

  /**
   * Returns the statistics for a given label.
   * If the label does not exist, returns an empty statistic object.
   * @param label The label to retrieve statistics for.
   * @returns The LabelStatistic object for the label.
   */
  public getLabelStatistic(label: string): LabelStatistic {
    if (!this.labelStatistics[label]) {
      this.labelStatistics[label] = {
        label,
        attemptCount: 0,
        weight: this.INITIAL_WEIGHT,
        lastAttemptDate: new Date().toISOString(),
      };
    }

    return this.labelStatistics[label];
  }

  /**
   * Chooses one or more words from the given list based on the adaptive scoring of their labels.
   * The choice is made by calculating a score for each word based on its labels,
   * and then selecting words randomly, weighted by their scores.
   * @param words - An array of LpWoordData objects representing the words to choose from.
   * @param count - The number of words to return. Defaults to 1.
   * @returns An array of randomly selected LpWoordData objects from the input tszlist, weighted by label scores.
   */
  public adaptiveChoice(words: LpWoordData[], count: number = 1): LpWoordData[] {
    if (count < 1) {
      throw new Error('Count must be at least 1');
    }

    if (words.length === 0) {
      return [];
    }

    // If count exceeds available words, just return all words (shuffled)
    if (count >= words.length) {
      return words.sort(() => Math.random() - 0.5).slice(0, count);
    }

    // Calculate a score for each word by averaging its labels' scores.
    const wordScores = words.map((word) => {
      const labels = word.tags
        .split(';')
        .map((l) => l.trim())
        .filter((l) => l);
      // if the word has no labels, give it a baseline weight
      if (labels.length === 0) {
        return { word, score: this.INITIAL_WEIGHT };
      }
      // const totalScore = labels.reduce((acc, label) => acc + this.computeLabelScore(label), 0);
      const totalScore = labels.reduce((acc, label) => acc + this.getLabelStatistic(label).weight, 0);
      const averageScore = totalScore / labels.length;
      return { word, score: averageScore };
    });

    // Make a shallow copy so we can remove selected words if needed
    const available = [...wordScores];
    const selected: LpWoordData[] = [];

    for (let i = 0; i < count; i++) {
      const totalWeight = available.reduce((sum, entry) => sum + entry.score, 0);
      const threshold = Math.random() * totalWeight;
      let cumulative = 0;
      let chosenIdx = 0;
      for (let j = 0; j < available.length; j++) {
        cumulative += available[j].score;
        if (cumulative >= threshold) {
          chosenIdx = j;
          break;
        }
      }
      selected.push(available[chosenIdx].word);
      available.splice(chosenIdx, 1); // Remove chosen word to avoid duplicates
    }

    return selected;
  }

  async adaptiveUpdate(word: LpWoordData, correct: boolean) {
    //Labels are called tags in CAS, and are seperated by semicolons
    const labels = word.tags.split(/[;,]/gi);

    const lastAttemptDate = new Date().toISOString();
    labels.forEach((label) => {
      const stats = this.getLabelStatistic(label);

      Object.assign(stats, {
        attemptCount: stats.attemptCount + 1,
        lastAttemptDate,
        weight: this.calculateNewWeight(stats.weight, correct ? 1 : 0),
      } satisfies Partial<LabelStatistic>);
    });

    this.saver.addPlaythroughObjective({
      name: word.word,
      properties: Object.fromEntries(labels.map((label) => [label, correct ? '1' : '0'])),
      score: correct ? '1' : '0',
    });

    //Save the new label statistics
    await this.saver.save({
      ...(await this.saver.load()),
      labelStatistics: Object.values(this.labelStatistics),
    });
  }

  private calculateNewWeight(previousWeight: number, score: number) {
    return (previousWeight * this.LAST_N_ATTEMPTS + score) / (this.LAST_N_ATTEMPTS + 1);
  }

  // TODO: @Ali is deze nog nodig nu we weight in de labelstatistic hebben?
  // private computeLabelScore(
  //   label: string,
  //   settings?: {
  //     numLastAttempts?: number;
  //     initialWeight?: number;
  //     performanceMaxBonus?: number;
  //     recencyMaxBonus?: number;
  //     recencyCap?: number;
  //   },
  // ): number {
  //   const now = Date.now();
  //   const stats = this.getLabelStatistic(label);
  //
  //   // Use settings overrides or fall back to defaults
  //   const numLastAttempts = settings?.numLastAttempts ?? this.LAST_N_ATTEMPTS;
  //   const initialWeight = settings?.initialWeight ?? this.INITIAL_WEIGHT;
  //   const performanceMaxBonus = settings?.performanceMaxBonus ?? this.PERFORMANCE_MAX_BONUS;
  //   const recencyMaxBonus = settings?.recencyMaxBonus ?? this.RECENCY_MAX_BONUS;
  //   const recencyCap = settings?.recencyCap ?? this.RECENCY_CAP;
  //
  //   if (stats.attempts.length === 0) {
  //     // unseen labels start off with a high weight
  //     return initialWeight;
  //   }
  //
  //   // only consider the last numLastAttempts
  //   const recentAttempts = stats.attempts.slice(-numLastAttempts);
  //   const totalAttempts = recentAttempts.length;
  //   const incorrectCount = recentAttempts.filter((a) => !a.correct).length;
  //
  //   // Performance factor: higher if more incorrect answers
  //   const performanceScore = (incorrectCount / totalAttempts) * performanceMaxBonus;
  //
  //   // Recency factor: we look at the most recent attempt
  //   const lastAttempt = new Date(recentAttempts[recentAttempts.length - 1].timestamp);
  //   let minutesSinceLast = (now - lastAttempt.getTime()) / (1000 * 60);
  //   if (minutesSinceLast < 0) {
  //     console.warn(`Last attempt for label "${label}" is in the future: ${lastAttempt.toISOString()}`);
  //     minutesSinceLast = 0;
  //   }
  //
  //   const cappedMinutes = Math.min(minutesSinceLast, recencyCap);
  //   const recencyScore = (cappedMinutes / recencyCap) * recencyMaxBonus;
  //
  //   return performanceScore + recencyScore;
  // }
}
